import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  useContext,
  type ParentComponent,
} from 'solid-js';
import { makePersisted } from '@solid-primitives/storage';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ThemeResolved = 'light' | 'dark';

interface ThemeContextValue {
  preference: () => ThemePreference;
  setPreference: (next: ThemePreference) => void;
  resolved: () => ThemeResolved;
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>();

const detectSystem = (): ThemeResolved =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';

export const ThemeProvider: ParentComponent = (props) => {
  const [preference, setPreference] = makePersisted(
    createSignal<ThemePreference>('dark'),
    { name: 'sportpark.theme', storage: localStorage },
  );
  const [systemTheme, setSystemTheme] = createSignal<ThemeResolved>(detectSystem());

  onMount(() => {
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setSystemTheme(mql.matches ? 'light' : 'dark');
    mql.addEventListener('change', handler);
    onCleanup(() => mql.removeEventListener('change', handler));
  });

  const resolved = createMemo<ThemeResolved>(() =>
    preference() === 'system' ? systemTheme() : (preference() as ThemeResolved),
  );

  createEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = resolved();
    document.documentElement.style.colorScheme = resolved();
  });

  const cycle = () => {
    const order: ThemePreference[] = ['light', 'dark', 'system'];
    const idx = order.indexOf(preference());
    setPreference(order[(idx + 1) % order.length] ?? 'system');
  };

  return (
    <ThemeContext.Provider value={{ preference, setPreference, resolved, cycle }}>
      {props.children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
};
